import PropTypes from 'prop-types';
import { labelValuePairProp, textProp } from '../../tools/customProps';
import { addressFieldProp } from './AddressFieldItem';

// Common prop types
export const textPairProp = PropTypes.shape({
  key: PropTypes.string,
  value: PropTypes.string,
});

export const TextPairType = PropTypes.shape({
  key: PropTypes.string,
  value: PropTypes.string,
});

export const ProductType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  sku: PropTypes.string,
  storeItem: PropTypes.string,
  price: PropTypes.string,
  formattedPrice: PropTypes.string,
  imageUrl: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  productUrl: PropTypes.string,
  collection: PropTypes.string,
  colorSequence: PropTypes.string,
  isSample: PropTypes.bool,
  isGiftBox: PropTypes.bool,
  isFreeGift: PropTypes.bool,
  isCharityItem: PropTypes.bool,
  isFinalSaleItem: PropTypes.bool,
  useKicUrl: PropTypes.bool,
});

export const OrderProductTextType = PropTypes.shape({
  size: TextPairType,
  color: TextPairType,
  item: TextPairType,
  storeItemNumber: TextPairType,
  finalSaleMessage: TextPairType,
  sku: TextPairType,
  cancelItem: TextPairType,
  returnForRefund: TextPairType,
  returnThisItem: TextPairType,
  exchangeButtonLabel: TextPairType,
  reserveAgain: TextPairType,
  itemsInReservation: TextPairType,
});

export const OrderProductType = PropTypes.shape({
  product: ProductType,
  departmentName: PropTypes.string,
  status: PropTypes.string,
  statusIcon: PropTypes.string,
  isCancellable: PropTypes.bool,
  quantity: PropTypes.string,
  totalPrice: PropTypes.number,
  salesTax: PropTypes.number,
  formattedSalesTax: PropTypes.string,
  totalItemsPrice: PropTypes.number,
  totalItemsPriceWithoutTax: PropTypes.number,
  formattedTotalItemsPriceWithoutTax: PropTypes.string,
  itemPrice: PropTypes.number,
  formattedItemPrice: PropTypes.string,
  itemPricePaid: PropTypes.number,
  formattedItemPricePaid: PropTypes.string,
  shippingTax: PropTypes.number,
  formattedShippingTax: PropTypes.string,
  packageNumber: PropTypes.string,
  expectedDeliveryDate: PropTypes.string,
  text: OrderProductTextType,
});

// Order Details Transaction Summary

export const TransactionRowType = PropTypes.shape({
  label: TextPairType,
  price: PropTypes.string,
  isNegative: PropTypes.bool,
  className: PropTypes.string,
});

export const transactionSummaryProps = PropTypes.shape({
  itemLabel: textProp,
  isNegative: PropTypes.bool,
  priceLabel: textProp,
  rows: PropTypes.arrayOf(TransactionRowType),
  footer: TransactionRowType,
  number: PropTypes.string,
});

export const transactionSummaryDefaultProps = {
  isNegative: false,
  footer: null,
};

// Resend Order Confirmation Button State
export const resendOrderButtonProps = PropTypes.shape({
  buttonText: textProp,
  processing: textProp,
  error: textProp,
  success: textProp,
  number: PropTypes.string,
});

export const resendOrderButtonDefaultProps = {
  buttonText: null,
  processing: null,
  error: null,
  success: null,
  number: null,
};

export const cancelOrderControlProps = {
  email: PropTypes.string,
  label: textProp,
  orderNumber: PropTypes.string,
};

// Order Details CTA Group Buttons
export const ctaGroupButtonsProps = {
  ctaGroup: PropTypes.arrayOf(labelValuePairProp),
  number: PropTypes.string,
  resendOrder: resendOrderButtonProps,
};

export const ctaGroupButtonsDefaultProps = {
  ctaGroup: [],
  resendOrder: null,
  number: null,
};

export const shippingAddress = {
  accessPointName: PropTypes.string,
  address1: PropTypes.string,
  address2: PropTypes.string,
  country: PropTypes.string,
  formattedCityStateZip: PropTypes.string,
  formattedName: PropTypes.string,
  mapUrl: labelValuePairProp,
};

export const shippingAddressCardProps = {
  shippingAddress: PropTypes.shape({
    title: textProp,
    shippedToAddress: PropTypes.shape(shippingAddress),
  }),
};

const billingInfoItem = PropTypes.shape({
  paymentIcon: PropTypes.string,
  paymentIconLabel: PropTypes.string,
  cardNumber: PropTypes.string,
  cardExpiration: PropTypes.string,
  paymentType: PropTypes.string,
  email: PropTypes.string,
  paymentURL: PropTypes.string,
  paymentStatus: PropTypes.string,
  billingAddress: PropTypes.shape(shippingAddress),
  konbiniLocationLabel: textProp,
});

export const billingInfoItemProps = {
  title: PropTypes.string,
  billingInfoItem,
};

export const billingInfoCardProps = {
  billingInfo: PropTypes.shape({
    title: textProp,
    billingInfoList: PropTypes.arrayOf(billingInfoItem),
  }),
};

export const productListProps = {
  orderProducts: PropTypes.arrayOf(OrderProductType),
  text: PropTypes.shape({
    size: TextPairType,
    color: TextPairType,
    item: TextPairType,
    storeItemNumber: TextPairType,
    finalSaleMessage: TextPairType,
    sku: TextPairType,
    cancelItem: TextPairType,
    returnForRefund: TextPairType,
    returnThisItem: TextPairType,
    exchangeButtonLabel: TextPairType,
    reserveAgain: TextPairType,
    itemsInReservation: TextPairType,
  }),
};

export const PackageType = PropTypes.shape({
  packageNumber: PropTypes.string,
  trackingNumber: PropTypes.string,
  dateReceived: PropTypes.string,
  carrier: PropTypes.string,
  trackingURL: PropTypes.string,
  productPrice: PropTypes.string,
  formattedProductPrice: PropTypes.string,
  shippingCharge: PropTypes.number,
  formattedShippingCharge: PropTypes.string,
  tax: PropTypes.number,
  formattedTax: PropTypes.string,
  additionalCharges: PropTypes.number,
  dateShipped: PropTypes.string,
  status: PropTypes.string,
  adjustments: PropTypes.number,
  formattedAdjustments: PropTypes.string,
  packageTotalPrice: PropTypes.number,
  formattedPackageTotalPrice: PropTypes.string,
  orderLineKeys: PropTypes.arrayOf(PropTypes.string),
});

export const packageGroupProps = {
  packageData: PropTypes.shape({
    packageNumber: PropTypes.string,
    trackingNumber: PropTypes.string,
    dateReceived: PropTypes.string,
    carrier: PropTypes.string,
    trackingURL: PropTypes.string,
    productPrice: PropTypes.string,
    formattedProductPrice: PropTypes.string,
    shippingCharge: PropTypes.number,
    formattedShippingCharge: PropTypes.string,
    tax: PropTypes.number,
    formattedTax: PropTypes.string,
    additionalCharges: PropTypes.number,
    dateShipped: PropTypes.string,
    status: PropTypes.string,
    statusIcon: PropTypes.string,
    adjustments: PropTypes.number,
    formattedAdjustments: PropTypes.string,
    packageTotalPrice: PropTypes.number,
    formattedPackageTotalPrice: PropTypes.string,
    orderLineKeys: PropTypes.arrayOf(PropTypes.string),
    packageSummaryData: transactionSummaryProps,
    packageTexts: PropTypes.shape({
      packageHeaderText: PropTypes.string,
      trackPackageText: PropTypes.string,
    }),
  }),
  allOrderProducts: PropTypes.arrayOf(PropTypes.shape({
    orderLineKey: PropTypes.string,
    product: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      sku: PropTypes.string,
      imageUrl: PropTypes.string,
      productUrl: PropTypes.string,
    }),
    text: PropTypes.shape({
      size: TextPairType,
      color: TextPairType,
      item: TextPairType,
      storeItemNumber: TextPairType,
      sku: TextPairType,
    }),
  })),
  text: PropTypes.shape({
    packageHeaderText: PropTypes.string,
    trackPackageText: PropTypes.string,
  }),
};

const extendedVatDetails = PropTypes.shape({
  vatIncludedInMerchPriceToCountry: textProp,
  vatDeductedNonCountryShipments: textProp,
  recipientNotLiableForImportDuties: textProp,
  vatRefundedForMerhandiseReturns: textProp,
});

export const vatDutyDetailsProps = {
  vatDutyDetails: PropTypes.shape({
    title: textProp,
    recipientNotChargedAddlDuties: textProp,
    extendedVatDetails,
    sandhVatNotRefundedMsg: textProp,
  }),
};

export const orderSummaryProps = {
  shippingDetails: shippingAddressCardProps.shippingAddress,
  billingInfo: billingInfoCardProps.billingInfo,
  vatDutyDetails: vatDutyDetailsProps.vatDutyDetails,
};

export const orderSummaryDefaultProps = {
  shippingDetails: null,
  billingInfo: null,
  vatDutyDetails: null,
};

export const orderItemsContainerProps = {
  orderProducts: PropTypes.arrayOf(OrderProductType),
  packages: PropTypes.arrayOf(PackageType),
  text: PropTypes.shape({
    packageHeaderText: PropTypes.string,
    trackPackageText: PropTypes.string,
    size: TextPairType,
    color: TextPairType,
    item: TextPairType,
    storeItemNumber: TextPairType,
    finalSaleMessage: TextPairType,
    sku: TextPairType,
    cancelItem: TextPairType,
    returnForRefund: TextPairType,
    returnThisItem: TextPairType,
    exchangeButtonLabel: TextPairType,
    reserveAgain: TextPairType,
    itemsInReservation: TextPairType,
  }),
};

export const orderItemsContainerDefaultProps = {
  packages: null,
  text: {},
};

export const orderItemProps = {
  orderProduct: OrderProductType,
  text: PropTypes.shape({
    size: TextPairType,
    color: TextPairType,
    item: TextPairType,
    storeItemNumber: TextPairType,
    finalSaleMessage: TextPairType,
    sku: TextPairType,
    cancelItem: TextPairType,
    returnForRefund: TextPairType,
    returnThisItem: TextPairType,
    exchangeButtonLabel: TextPairType,
    reserveAgain: TextPairType,
    itemsInReservation: TextPairType,
  }),
};

export const orderDetailsDataProps = {
  queryData: PropTypes.object,
};

export const orderDetailsDataDefaultProps = {
  queryData: {},
};

export const rneOrderDetailsDataProps = {
  rneQueryData: PropTypes.object,
};

export const rneOrderDetailsDataDefaultProps = {
  rneQueryData: {},
};

export const addressFieldsPropTypes = {
  fields: PropTypes.arrayOf(addressFieldProp),
  onChange: PropTypes.func,
  addressFormData: PropTypes.shape({}),
};

export const addressFieldsDefaultProps = {
  fields: [],
  onChange: () => {},
  addressFormData: {},
};

// WSS Payment Update Form Props
export const wssAddressProp = PropTypes.shape({
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postalCode: PropTypes.string,
  zipCode: PropTypes.string,
  country: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  province: PropTypes.string,
  isBillingAllowed: PropTypes.bool,
  isShippingAllowed: PropTypes.bool,
  isDefaultBilling: PropTypes.bool,
  isDefaultShipping: PropTypes.bool,
});

export const wssPaymentDetailsProp = PropTypes.shape({
  grandTotalFormatted: PropTypes.string,
  authAmountFormatted: PropTypes.string,
  currencyPattern: PropTypes.string,
  updateCardInformationTitle: textPairProp,
  billingAddressTitle: textPairProp,
  billToShippingAddressLabel: textPairProp,
  updateButtonText: textPairProp,
  wssCardLabel: textPairProp,
  shippingAddress: wssAddressProp,
  defaultBillingAddress: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    province: PropTypes.string,
    postalCode: PropTypes.string,
    zipCode: PropTypes.string,
    country: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  billingCountryList: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.string,
  })),
});

export const wssPaymentUpdateFormProps = {
  orderNumber: PropTypes.string,
  wssPaymentDetails: wssPaymentDetailsProp,
  onPaymentError: PropTypes.func,
  onPaymentSuccess: PropTypes.func,
  handlePaymentUpdateCompletedOverride: PropTypes.func,
};

export const wssPaymentUpdateFormDefaultProps = {
  orderNumber: '',
  wssPaymentDetails: null,
  onPaymentError: undefined,
  onPaymentSuccess: undefined,
  handlePaymentUpdateCompletedOverride: undefined,
};

// Country Selector Props
export const countryListItemProp = PropTypes.shape({
  value: PropTypes.string,
  label: PropTypes.string,
});

export const countrySelectorProps = {
  countryList: PropTypes.arrayOf(countryListItemProp),
  selectedCountry: PropTypes.string,
  onCountryChange: PropTypes.func,
  label: PropTypes.string,
  isInvalid: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export const countrySelectorDefaultProps = {
  countryList: [],
  selectedCountry: '',
  onCountryChange: () => {},
  label: 'Country',
  isInvalid: false,
  errorMessage: '',
};

// CVV Field Props
export const cvvFieldProps = {
  cvv: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  hasValidationError: PropTypes.bool,
  cardBrand: PropTypes.string,
  index: PropTypes.number,
  tooltipMessage: PropTypes.string,
  tooltipImage: PropTypes.string,
};

export const cvvFieldDefaultProps = {
  cvv: '',
  onChange: () => {},
  label: 'CVV',
  errorMessage: '',
  hasValidationError: false,
  cardBrand: '021',
  index: 0,
  tooltipMessage: '',
  tooltipImage: '',
};

// Expiry Field Props
export const expiryFieldProps = {
  expiryDate: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  hasValidationError: PropTypes.bool,
  index: PropTypes.number,
};

export const expiryFieldDefaultProps = {
  expiryDate: '',
  onChange: () => {},
  label: 'Expiry Date',
  errorMessage: '',
  hasValidationError: false,
  index: 0,
};
